import { TournamentGame, TournamentPool, TournamentStanding } from '../types';
import { getBackendUrl } from '../config';
import { authenticatedFetch } from './apiClient';

export const HOWIES_CUP_2026_SCHEDULE_URL = 'https://my.200x85.com/tournaments/b242def3-6e03-4dba-8a7f-6b5960f35df0/teams/94e4431b-9173-4d1a-a686-7a4fb8ee13cb';
export const HOWIES_CUP_2026_STANDINGS_URL = 'https://my.200x85.com/tournaments/b242def3-6e03-4dba-8a7f-6b5960f35df0/standings?division=895';

export const HOWIES_CUP_2025_SCHEDULE_URL = 'https://my.200x85.com/tournaments/44eaf589-fcfb-4a79-8654-54d45459c333/teams/94e4431b-9173-4d1a-a686-7a4fb8ee13cb';
export const HOWIES_CUP_2025_STANDINGS_URL = 'https://my.200x85.com/tournaments/44eaf589-fcfb-4a79-8654-54d45459c333/standings?division=578';

export const MOTOWN_2025_SCHEDULE_URL = 'https://my.200x85.com/tournaments/4a17c3fc-c6af-4605-9fd6-7d35d1f48193/teams/94e4431b-9173-4d1a-a686-7a4fb8ee13cb';
export const MOTOWN_2025_STANDINGS_URL = 'https://my.200x85.com/tournaments/4a17c3fc-c6af-4605-9fd6-7d35d1f48193/standings?division=641';

export const CCM_WORLD_INVITE_2026_SCHEDULE_URL = 'https://my.200x85.com/tournaments/1a0a158e-5bf4-4e1e-93a9-01bb7e7963ba/teams/94e4431b-9173-4d1a-a686-7a4fb8ee13cb';
export const CCM_WORLD_INVITE_2026_STANDINGS_URL = 'https://my.200x85.com/tournaments/1a0a158e-5bf4-4e1e-93a9-01bb7e7963ba/standings?division=994';

/**
 * Parses tournament schedule HTML string into structured TournamentGame array.
 * Supports both modern 2026 Regystra card layout and legacy table layouts.
 */
export function parseTournamentScheduleHtml(html: string): TournamentGame[] {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const games: TournamentGame[] = [];

    // 1. Modern Card-based layout (2026 200x85/Regystra)
    const cards = doc.querySelectorAll('#schedule .card-body > .card, #schedule .card.pointer, .card-animate');
    if (cards.length > 0) {
        cards.forEach((card, index) => {
            const onclickAttr = card.getAttribute('onclick') || '';
            const boxscoreUrlMatch = onclickAttr.match(/'([^']+)'/);
            const boxscoreUrl = boxscoreUrlMatch ? boxscoreUrlMatch[1] : '';

            const header = card.querySelector('.card-header');
            let dateStr = '';
            let timeStr = '';
            let statusStr = '';
            let locationStr = '';

            if (header) {
                const dateEl = header.querySelector('i.fa-calendar-alt')?.parentElement;
                const timeEl = header.querySelector('i.fa-clock')?.parentElement;
                const locEl = header.querySelector('i.fa-map-marker-alt')?.parentElement;
                const statusEl = header.querySelector('.game-scheduled, .text-uppercase, [class*="game-"]');

                dateStr = dateEl ? dateEl.textContent?.trim() || '' : '';
                timeStr = timeEl ? timeEl.textContent?.trim() || '' : '';
                locationStr = locEl ? locEl.textContent?.trim() || '' : '';
                statusStr = statusEl ? statusEl.textContent?.trim() || '' : '';
            }

            const body = card.querySelector('.card-body');
            let homeTeam = '';
            let visitorTeam = '';
            let homeScore = '-';
            let visitorScore = '-';

            if (body) {
                const teamCols = body.querySelectorAll('.col-5, .col-md-4');
                if (teamCols.length >= 2) {
                    const homeP = teamCols[0].querySelector('p.fw-bold, p.text-uppercase');
                    const visitorP = teamCols[teamCols.length - 1].querySelector('p.fw-bold, p.text-uppercase');
                    homeTeam = homeP?.textContent?.trim() || '';
                    visitorTeam = visitorP?.textContent?.trim() || '';
                }

                const isScheduled = /scheduled/i.test(statusStr);
                if (!isScheduled) {
                    const centerCol = teamCols.length >= 3 ? teamCols[1] : body.querySelector('.col-2, .col-md-4');
                    const centerText = centerCol?.textContent?.trim() || '';
                    const scoreMatch = !/AM|PM/i.test(centerText) ? centerText.match(/(\d+)\s*[-–]\s*(\d+)/) : null;
                    if (scoreMatch) {
                        homeScore = scoreMatch[1];
                        visitorScore = scoreMatch[2];
                    }
                }
            }

            const parts = boxscoreUrl.split('/').filter(Boolean);
            const gameId = parts[parts.indexOf('games') + 1] || card.getAttribute('data-id') || `game-${index}`;
            const dateTime = `${dateStr} ${timeStr}`.trim();

            games.push({
                id: gameId,
                dateTime,
                homeTeam,
                visitorTeam,
                homeScore,
                visitorScore,
                status: statusStr || 'scheduled',
                location: locationStr,
                boxscoreUrl,
            });
        });
        return games;
    }

    // 2. Legacy Table-based layout
    const gameRows = doc.querySelectorAll('#schedule .card-body table tbody tr, table.schedule tbody tr');
    gameRows.forEach((row, index) => {
        const cells = row.querySelectorAll('td');
        if (cells.length < 8) return;

        const onclickAttr = row.getAttribute('onclick');
        const boxscoreUrlMatch = onclickAttr ? onclickAttr.match(/'([^']+)'/) : null;
        const boxscoreUrl = boxscoreUrlMatch ? boxscoreUrlMatch[1] : '';

        const game: TournamentGame = {
            id: row.getAttribute('data-id') || `game-${index}`,
            dateTime: cells[1]?.textContent?.trim() || '',
            homeTeam: cells[2]?.textContent?.trim() || '',
            homeScore: cells[3]?.textContent?.trim() || '',
            status: cells[4]?.textContent?.trim() || '',
            visitorScore: cells[5]?.textContent?.trim() || '',
            visitorTeam: cells[6]?.textContent?.trim() || '',
            location: cells[7]?.textContent?.trim() || '',
            boxscoreUrl: boxscoreUrl,
        };
        games.push(game);
    });

    return games;
}

/**
 * Parses tournament standings HTML string into structured TournamentPool array.
 */
export function parseTournamentStandingsHtml(html: string): TournamentPool[] {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const standingsTables = doc.querySelectorAll('table.standings, table#standings');
    const pools: TournamentPool[] = [];

    standingsTables.forEach(table => {
        const theadTh = table.querySelector('thead th')?.textContent?.trim();
        const parentCard = table.closest('.card');
        const cardTitle = parentCard?.querySelector('.card-title')?.textContent?.trim();
        const poolName = (theadTh && theadTh !== 'Team') ? theadTh : (cardTitle || 'Standings');

        const teamRows = table.querySelectorAll('tbody tr');
        const standings: TournamentStanding[] = [];

        teamRows.forEach(row => {
            const cells = row.querySelectorAll('td');
            if (cells.length < 10) return;

            const teamLink = cells[0].querySelector('a');
            const teamName = teamLink?.textContent?.trim() || cells[0]?.textContent?.trim() || '';
            const teamUrl = teamLink?.href || undefined;

            const standing: TournamentStanding = {
                teamName,
                teamUrl,
                gp: cells[1]?.textContent?.trim() || '0',
                w: cells[2]?.textContent?.trim() || '0',
                t: cells[3]?.textContent?.trim() || '0',
                l: cells[4]?.textContent?.trim() || '0',
                otw: cells[5]?.textContent?.trim() || '0',
                otl: cells[6]?.textContent?.trim() || '0',
                pts: cells[7]?.textContent?.trim() || '0',
                gf: cells[8]?.textContent?.trim() || '0',
                ga: cells[9]?.textContent?.trim() || '0',
                gd: cells[10]?.textContent?.trim() || '0',
                quot: cells[11]?.textContent?.trim() || '0.000',
                seed: cells[cells.length - 1]?.textContent?.trim() || '-',
            };
            standings.push(standing);
        });
        pools.push({ poolName, standings });
    });

    return pools;
}

/**
 * Fetches and parses tournament schedule from a remote URL via the backend scrape proxy.
 */
export const fetchTournamentSchedule = async (scheduleUrl: string): Promise<TournamentGame[]> => {
    const PROXY_ENDPOINT = `${getBackendUrl()}/api/scrape?url=${encodeURIComponent(scheduleUrl)}`;

    try {
        const response = await authenticatedFetch(PROXY_ENDPOINT, { cache: 'no-store' });
        if (!response.ok) {
            let errorMessage = `Failed to fetch schedule via proxy. Status: ${response.status}`;
            try {
                const errorJson = await response.json();
                if (errorJson.error) {
                    errorMessage = errorJson.error;
                }
            } catch (e) { /* Ignore if response is not JSON */ }
            throw new Error(errorMessage);
        }
        const html = await response.text();
        return parseTournamentScheduleHtml(html);
    } catch (error: any) {
        console.error("Error scraping tournament schedule:", error);
        throw new Error(error.message || "Could not retrieve tournament schedule. The scraping service may be temporarily unavailable.");
    }
};

/**
 * Fetches and parses tournament standings from a remote URL via the backend scrape proxy.
 */
export const fetchTournamentStandings = async (standingsUrl: string): Promise<TournamentPool[]> => {
    const PROXY_ENDPOINT = `${getBackendUrl()}/api/scrape?url=${encodeURIComponent(standingsUrl)}`;

    try {
        const response = await authenticatedFetch(PROXY_ENDPOINT, { cache: 'no-store' });
        if (!response.ok) {
            let errorMessage = `Failed to fetch standings via proxy. Status: ${response.status}`;
            try {
                const errorJson = await response.json();
                if (errorJson.error) errorMessage = errorJson.error;
            } catch (e) { /* Ignore if response is not JSON */ }
            throw new Error(errorMessage);
        }
        const html = await response.text();
        return parseTournamentStandingsHtml(html);
    } catch (error: any) {
        console.error("Error scraping tournament standings:", error);
        throw new Error(error.message || "Could not retrieve tournament standings. The scraping service may be temporarily unavailable.");
    }
};

/**
 * Fetches and parses the Howie's Hometown Cup schedule for the Jets.
 * Defaults to current 2026 season.
 */
export const fetchHowiesCupSchedule = async (year: number | string = 2026, force?: boolean): Promise<TournamentGame[]> => {
    const key = year === 2025 || year === '2025' || year === 'howies-hometown-cup-2025'
        ? 'howies-hometown-cup-2025'
        : 'howies-hometown-cup-2026';
    try {
        const data = await fetchTournamentData(key, { force });
        if (data.games && data.games.length > 0) return data.games;
    } catch {
        // Fallback to client proxy
    }
    const url = key === 'howies-hometown-cup-2025'
        ? HOWIES_CUP_2025_SCHEDULE_URL
        : HOWIES_CUP_2026_SCHEDULE_URL;
    return fetchTournamentSchedule(url);
};

/**
 * Fetches and parses the Howie's Hometown Cup standings.
 * Defaults to current 2026 season.
 */
export const fetchHowiesCupStandings = async (year: number | string = 2026, force?: boolean): Promise<TournamentPool[]> => {
    const key = year === 2025 || year === '2025' || year === 'howies-hometown-cup-2025'
        ? 'howies-hometown-cup-2025'
        : 'howies-hometown-cup-2026';
    try {
        const data = await fetchTournamentData(key, { force });
        if (data.pools && data.pools.length > 0) return data.pools;
    } catch {
        // Fallback to client proxy
    }
    const url = key === 'howies-hometown-cup-2025'
        ? HOWIES_CUP_2025_STANDINGS_URL
        : HOWIES_CUP_2026_STANDINGS_URL;
    return fetchTournamentStandings(url);
};

/**
 * Fetches and parses the CCM Motown schedule for the Jets.
 */
export const fetchMotownSchedule = async (force?: boolean): Promise<TournamentGame[]> => {
    try {
        const data = await fetchTournamentData('ccm-motown-2025', { force });
        if (data.games && data.games.length > 0) return data.games;
    } catch {
        // Fallback to client proxy
    }
    return fetchTournamentSchedule(MOTOWN_2025_SCHEDULE_URL);
};

/**
 * Fetches and parses the CCM Motown standings.
 */
export const fetchMotownStandings = async (force?: boolean): Promise<TournamentPool[]> => {
    try {
        const data = await fetchTournamentData('ccm-motown-2025', { force });
        if (data.pools && data.pools.length > 0) return data.pools;
    } catch {
        // Fallback to client proxy
    }
    return fetchTournamentStandings(MOTOWN_2025_STANDINGS_URL);
};

/**
 * Fetches and parses the CCM World Invite schedule for the Jets.
 */
export const fetchCcmWorldInviteSchedule = async (force?: boolean): Promise<TournamentGame[]> => {
    try {
        const data = await fetchTournamentData('ccm-world-invite-2026', { force });
        if (data.games && data.games.length > 0) return data.games;
    } catch {
        // Fallback to client proxy
    }
    return fetchTournamentSchedule(CCM_WORLD_INVITE_2026_SCHEDULE_URL);
};

/**
 * Fetches and parses the CCM World Invite standings.
 */
export const fetchCcmWorldInviteStandings = async (force?: boolean): Promise<TournamentPool[]> => {
    try {
        const data = await fetchTournamentData('ccm-world-invite-2026', { force });
        if (data.pools && data.pools.length > 0) return data.pools;
    } catch {
        // Fallback to client proxy
    }
    return fetchTournamentStandings(CCM_WORLD_INVITE_2026_STANDINGS_URL);
};

export interface StructuredTournamentResponse {
    tournamentKey: string;
    name: string;
    startDate?: string;
    endDate?: string;
    updatedAt: string;
    cached?: boolean;
    isLive?: boolean;
    stale?: boolean;
    warning?: string;
    error?: string;
    games: TournamentGame[];
    pools: TournamentPool[];
}

export interface TournamentDetails {
    games: TournamentGame[];
    standings: TournamentPool[];
    isScheduleAvailable: boolean;
    updatedAt?: string;
    isLive?: boolean;
    stale?: boolean;
    cached?: boolean;
}

export const TOURNAMENT_CACHE_PREFIX = 'chicagojets_tournament_';

export const getCachedTournamentData = (key: string): StructuredTournamentResponse | null => {
    try {
        const raw = localStorage.getItem(`${TOURNAMENT_CACHE_PREFIX}${key}`);
        if (raw) return JSON.parse(raw);
    } catch {
        // Ignore localStorage errors
    }
    return null;
};

export const setCachedTournamentData = (key: string, data: StructuredTournamentResponse): void => {
    try {
        localStorage.setItem(`${TOURNAMENT_CACHE_PREFIX}${key}`, JSON.stringify(data));
    } catch {
        // Ignore localStorage errors
    }
};

/**
 * Centrally fetches structured tournament data from backend API with Firestore write-through caching.
 */
export const fetchTournamentData = async (
    tournamentKey: string,
    options?: { scheduleUrl?: string; standingsUrl?: string; force?: boolean }
): Promise<StructuredTournamentResponse> => {
    const params = new URLSearchParams();
    if (options?.force) params.set('force', 'true');
    if (options?.scheduleUrl) params.set('scheduleUrl', options.scheduleUrl);
    if (options?.standingsUrl) params.set('standingsUrl', options.standingsUrl);

    const query = params.toString() ? `?${params.toString()}` : '';
    const endpoint = `${getBackendUrl()}/api/tournaments/${tournamentKey}${query}`;

    try {
        const response = await authenticatedFetch(endpoint, { cache: 'no-store' });
        if (response.ok && typeof response.json === 'function') {
            const data: StructuredTournamentResponse = await response.json();
            setCachedTournamentData(tournamentKey, data);
            return data;
        }
    } catch (err) {
        console.warn(`[tournamentScraper] Failed to fetch /api/tournaments/${tournamentKey}:`, err);
    }

    // Fallback 1: check localStorage cache
    const cached = getCachedTournamentData(tournamentKey);
    if (cached) {
        return { ...cached, stale: true };
    }

    // Fallback 2: legacy client scrape proxy
    const schedUrl = options?.scheduleUrl || (
        tournamentKey === 'howies-hometown-cup-2026' ? HOWIES_CUP_2026_SCHEDULE_URL :
        tournamentKey === 'howies-hometown-cup-2025' ? HOWIES_CUP_2025_SCHEDULE_URL :
        tournamentKey === 'ccm-world-invite-2026' ? CCM_WORLD_INVITE_2026_SCHEDULE_URL :
        tournamentKey === 'ccm-motown-2025' ? MOTOWN_2025_SCHEDULE_URL : undefined
    );
    const standUrl = options?.standingsUrl || (
        tournamentKey === 'howies-hometown-cup-2026' ? HOWIES_CUP_2026_STANDINGS_URL :
        tournamentKey === 'howies-hometown-cup-2025' ? HOWIES_CUP_2025_STANDINGS_URL :
        tournamentKey === 'ccm-world-invite-2026' ? CCM_WORLD_INVITE_2026_STANDINGS_URL :
        tournamentKey === 'ccm-motown-2025' ? MOTOWN_2025_STANDINGS_URL : undefined
    );

    if (schedUrl || standUrl) {
        const [games, pools] = await Promise.all([
            schedUrl ? fetchTournamentSchedule(schedUrl).catch(() => []) : Promise.resolve([]),
            standUrl ? fetchTournamentStandings(standUrl).catch(() => []) : Promise.resolve([]),
        ]);
        const fallbackPayload: StructuredTournamentResponse = {
            tournamentKey,
            name: tournamentKey,
            updatedAt: new Date().toISOString(),
            cached: false,
            isLive: false,
            games,
            pools,
        };
        setCachedTournamentData(tournamentKey, fallbackPayload);
        return fallbackPayload;
    }

    throw new Error(`Could not retrieve tournament data for '${tournamentKey}'.`);
};

/**
 * Centrally fetches tournament schedule and standings for any tournament key or TournamentInfo.
 */
export const fetchTournamentDetails = async (
    tournamentKeyOrInfo: string | { key: string; scheduleUrl?: string; standingsUrl?: string },
    options?: { force?: boolean }
): Promise<TournamentDetails> => {
    const key = typeof tournamentKeyOrInfo === 'string' ? tournamentKeyOrInfo : tournamentKeyOrInfo.key;
    const scheduleUrl = typeof tournamentKeyOrInfo === 'object' ? tournamentKeyOrInfo.scheduleUrl : undefined;
    const standingsUrl = typeof tournamentKeyOrInfo === 'object' ? tournamentKeyOrInfo.standingsUrl : undefined;

    try {
        const data = await fetchTournamentData(key, {
            scheduleUrl,
            standingsUrl,
            force: options?.force,
        });

        return {
            games: data.games || [],
            standings: data.pools || [],
            isScheduleAvailable: (data.games || []).length > 0,
            updatedAt: data.updatedAt,
            isLive: data.isLive,
            stale: data.stale,
            cached: data.cached,
        };
    } catch (e) {
        console.warn(`Could not fetch details for tournament ${key}:`, e);
        return { games: [], standings: [], isScheduleAvailable: false };
    }
};